import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { Check, ArrowLeft, Minus, Plus, ShoppingCart, FileText, ZoomIn, Star, Truck, ShieldCheck, Recycle } from "lucide-react";
import { getProduct, getByCategory, fmtINR } from "@/data/catalog";
import { useCart } from "@/context/CartContext";

function Selector({ label, options, value, onChange, testid }) {
  return (
    <div>
      <p className="overline text-royal mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={value === o ? "rounded-full px-4 py-2 text-sm border transition-colors bg-navy text-white border-navy" : "rounded-full px-4 py-2 text-sm border transition-colors border-border hover:bg-secondary"}>{o}</button>
        ))}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [zoom, setZoom] = useState(false);

  const [size, setSize] = useState(product?.sizes?.[0] || "");
  const [color, setColor] = useState(product?.colors?.[0] || "");
  const [qty, setQty] = useState(product?.moq || 50);

  if (!product) return <Navigate to="/products" replace />;
  const related = getByCategory(product.categorySlug).filter((p) => p.slug !== slug).slice(0, 4);

  const cartItem = () => ({ slug: product.slug, name: product.name, categorySlug: product.categorySlug, image: product.image, price: product.price, size, color, quantity: qty });

  const addToCart = () => { addItem(cartItem()); toast.success(`${product.name} added to cart`); };

  const requestQuoteWithProduct = () => {
    const params = new URLSearchParams({
      product: product.name,
      category: product.category,
      size: size || "",
      color: color || "",
      quantity: qty
    });
    navigate(`/request-quote?${params.toString()}`);
  };

  return (
    <div data-testid="product-detail" className="pt-32 lg:pt-40">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <Link to={`/products?category=${product.categorySlug}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-royal transition-colors" data-testid="back-link"><ArrowLeft size={16} /> {product.category}</Link>

        <div className="mt-6 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="relative overflow-hidden rounded-4xl border border-border group cursor-zoom-in" onClick={() => setZoom(true)} data-testid="product-image">
              <img src={product.image} alt={product.name} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" />
              <span className="absolute bottom-4 right-4 grid place-items-center h-11 w-11 rounded-full glass"><ZoomIn size={18} /></span>
              <span className="absolute top-4 left-4 rounded-full glass px-3 py-1 text-xs font-mono-accent uppercase tracking-wider">{product.tagline}</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <Link to={`/products?category=${product.categorySlug}`} className="text-sm text-royal font-medium">{product.category}</Link>
            <h1 className="mt-2 font-heading text-3xl lg:text-5xl font-extrabold text-foreground">{product.name}</h1>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex gap-0.5 text-emerald">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={15} fill="currentColor" />)}</div>
              <span className="text-sm text-muted-foreground">Trusted by 500+ brands</span>
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-heading text-3xl font-extrabold text-foreground" data-testid="product-price">{fmtINR(product.price)}</span>
              <span className="text-sm text-muted-foreground">/ unit · MOQ {product.moq}</span>
            </div>
            <p className="mt-4 text-muted-foreground leading-relaxed">{product.description}</p>

            <div className="mt-7 space-y-5">
              <Selector label="Size" options={product.sizes} value={size} onChange={setSize} testid="size" />
              <Selector label="Colour" options={product.colors} value={color} onChange={setColor} testid="color" />
              <div>
                <p className="overline text-royal mb-2">Quantity</p>
                <div className="inline-flex items-center border border-border rounded-full">
                  <button onClick={() => setQty((q) => Math.max(1, q - 10))} className="grid place-items-center h-11 w-11" data-testid="qty-dec"><Minus size={16} /></button>
                  <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center bg-transparent focus:outline-none font-semibold" data-testid="qty-input" />
                  <button onClick={() => setQty((q) => q + 10)} className="grid place-items-center h-11 w-11" data-testid="qty-inc"><Plus size={16} /></button>
                </div>
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button onClick={addToCart} data-testid="add-to-cart" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-navy text-navy dark:text-white dark:border-white px-6 py-4 font-semibold hover:bg-secondary transition-colors"><ShoppingCart size={18} /> Add to Cart</button>
              <button onClick={requestQuoteWithProduct} data-testid="buy-now" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-6 py-4 font-semibold hover:bg-royal transition-colors disabled:opacity-60"><FileText size={18} /> Request Quote</button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs text-muted-foreground">
              <div className="rounded-2xl border border-border p-3"><Truck size={18} className="mx-auto text-royal" /><span className="mt-1.5 block">Fast Delivery</span></div>
              <div className="rounded-2xl border border-border p-3"><ShieldCheck size={18} className="mx-auto text-royal" /><span className="mt-1.5 block">Quality Assured</span></div>
              <div className="rounded-2xl border border-border p-3"><Recycle size={18} className="mx-auto text-royal" /><span className="mt-1.5 block">Eco Options</span></div>
            </div>
          </Reveal>
        </div>

        {/* Specs & FAQ */}
        <section className="py-16 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground mb-6">Specifications</h2>
            <dl className="space-y-3">
              {[["Materials", product.materials.join(", ")], ["Sizes", product.sizes.join(", ")], ["Colours", product.colors.join(", ")], ["Applications", product.applications.join(", ")], ["MOQ", `${product.moq} units`]].map(([k, v]) => (
                <div key={k} className="flex gap-4 border-b border-border pb-3">
                  <dt className="w-32 shrink-0 font-mono-accent text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{k}</dt>
                  <dd className="text-foreground text-sm">{v}</dd>
                </div>
              ))}
            </dl>
            <h3 className="mt-8 font-heading text-lg font-bold text-foreground mb-3">Key Benefits</h3>
            <div className="grid grid-cols-2 gap-2">
              {product.benefits.map((b) => <span key={b} className="flex items-center gap-2 text-sm text-foreground"><Check size={15} className="text-emerald shrink-0" />{b}</span>)}
            </div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground mb-6">FAQs</h2>
            <Accordion type="single" collapsible data-testid="product-faq">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-10">
            <h2 className="font-heading text-2xl font-extrabold text-foreground mb-8">You may also like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} className="group block rounded-3xl border border-border overflow-hidden hover:-translate-y-1 transition-transform" data-testid={`related-${p.slug}`}>
                  <div className="overflow-hidden aspect-square"><img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="p-4"><h3 className="font-heading font-bold text-sm text-foreground group-hover:text-royal transition-colors line-clamp-1">{p.name}</h3><p className="text-xs font-mono-accent text-royal mt-1">{fmtINR(p.price)}</p></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {zoom && (
        <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm grid place-items-center p-6" onClick={() => setZoom(false)} data-testid="zoom-overlay">
          <img src={product.image} alt={product.name} className="max-h-[90vh] max-w-full rounded-2xl object-contain" />
        </div>
      )}
    </div>
  );
}
